/* eslint-disable fp/no-mutating-methods */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { groupBy } from 'ramda'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { selectRoles, selectUseEvents } from 'src/v3/settings/settings.slice'
import AddIcon from '@mui/icons-material/Add'
import {
  BulletedListIcon,
  CalendarIcon,
  CancelIcon,
  ClockIcon,
  ConfirmIcon,
  LocationIcon,
  SearchIcon,
} from 'src/v3/core/icons'
import {
  AccreditationType,
  accreditationDict,
} from 'src/v3/events/accreditations'
import { Event, EventSession, EventTypeName } from '../core/types'
import { DeleteIcon, EditIcon, UserIcon } from '../core/icons'
import {
  fetchAccreditations,
  fetchEventAccreditations,
  fetchEvents,
  fetchSessions,
  selectAccreditationsByType,
  selectAccreditationsStatus,
  selectEventAccreditations,
  selectEventAccreditationsStatus,
  selectEvents,
  selectEventsStatus,
  selectSessions,
  selectSessionsStatus,
} from './events.slice'
import axios from 'src/utilities/http-client'

dayjs.extend(utc)

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let [nameFilter, setNameFilter] = useState<string>('')
  let [eventTypeFilter, setEventTypeFilter] = useState<EventTypeName | 'All'>(
    'All',
  )
  let [dateLowerFilter, setDateLowerFilter] = useState<any>(dayjs())
  let [dateUpperFilter, setDateUpperFilter] = useState<any>(
    dayjs().add(12, 'month').format(),
  )
  let [accreditationFilter, setAccreditationFilter] = useState<
    AccreditationType | 'All'
  >('All')
  let [viewMode, setViewMode] = useState<string>('List')
  let [deleteEvent, setDeleteEvent] = useState<Event>()
  let [loading, setLoading] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let roles = useAppSelector(selectRoles)
  let useEvents = useAppSelector(selectUseEvents)

  let events = useAppSelector(selectEvents)
  let eventsStatus = useAppSelector(selectEventsStatus)
  let sessions = useAppSelector(selectSessions)
  let sessionsStatus = useAppSelector(selectSessionsStatus)
  let accreditations = useAppSelector(selectAccreditationsByType)
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)
  let eventAccreditations = useAppSelector(selectEventAccreditations)
  let eventAccreditationsStatus = useAppSelector(
    selectEventAccreditationsStatus,
  )

  let featuredEvents = events.filter((e) => e.featured)
  let foamfratEvents = events.filter((e) => !e.linkedServiceID)
  // eslint-disable-next-line fp/no-mutation
  featuredEvents = [...featuredEvents, ...foamfratEvents].slice(0, 3)
  let unscheduledEvents = events.filter(
    (e) =>
      !sessions.some((s) => s.eventID === e.id) &&
      (!nameFilter ||
        e.name.toLowerCase().includes(nameFilter.toLowerCase())) &&
      (eventTypeFilter === 'All' || e.eventType === eventTypeFilter) &&
      (accreditationFilter === 'All' ||
        accreditations[accreditationFilter].some((a) =>
          eventAccreditations.some((ea) => ea.accreditationID === a.id),
        )),
  )

  let sessionsByMonth = groupBy(
    (session: EventSession) => dayjs(session.sessionTime).format('MMMM YYYY'),
    sessions.filter((s) => {
      let sessionEvent = events.find((e) => e.id === s.eventID)
      return (
        // sessionEvent?.linkedServiceID &&
        (!nameFilter ||
          sessionEvent?.name
            .toLowerCase()
            .includes(nameFilter.toLowerCase())) &&
        (eventTypeFilter === 'All' ||
          sessionEvent?.eventType === eventTypeFilter) &&
        dayjs(s.sessionTime).isBefore(dayjs(dateUpperFilter)) &&
        dayjs(s.sessionTime).isAfter(dayjs(dateLowerFilter)) &&
        (accreditationFilter === 'All' ||
          accreditations[accreditationFilter].some((a) =>
            eventAccreditations.some(
              (ea) =>
                ea.accreditationID === a.id && ea.eventID === sessionEvent?.id,
            ),
          ))
      )
    }),
  )

  let sortedMonths = Object.keys(sessionsByMonth).sort((a, b) =>
    dayjs(sessionsByMonth[a]?.[0].sessionTime).isAfter(
      dayjs(sessionsByMonth[b]?.[0].sessionTime),
    )
      ? 1
      : -1,
  )

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus])

  useEffect(() => {
    if (sessionsStatus === 'uninitialized') {
      dispatch(fetchSessions())
    }
  }, [sessionsStatus])

  useEffect(() => {
    if (accreditationsStatus === 'uninitialized') {
      dispatch(fetchAccreditations())
    }
  }, [accreditationsStatus])

  useEffect(() => {
    if (eventAccreditationsStatus === 'uninitialized') {
      dispatch(fetchEventAccreditations())
    }
  }, [eventAccreditationsStatus])

  let isLoaded = [
    eventsStatus,
    sessionsStatus,
    accreditationsStatus,
    eventAccreditationsStatus,
  ].every((s) => s === 'fulfilled' || s === 'rejected')

  return useEvents ? (
    <Container maxWidth="xl">
      <Box sx={{ mt: -20 }}></Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          mb: 2,
          gap: 1,
        }}
      >
        <Typography variant="h4">Events</Typography>
        <Button
          size="large"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/events/create')}
        >
          Create Event
        </Button>
      </Box>
      {(featuredEvents.length > 0 || !isLoaded) && (
        <Typography sx={{ mb: 1 }} variant="h6">
          Featured Events
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 2,
          overflow: 'scroll',
        }}
      >
        {featuredEvents.map((event) => {
          let hours = Math.floor(event.hours)
          let minutes = (event.hours - hours) * 60
          let duration = minutes ? `${hours}h ${minutes}m` : `${hours}h`

          let eventSessions = sessions
            .filter((s) => s.eventID === event.id)
            .sort((a, b) =>
              dayjs(a.sessionTime).isAfter(dayjs(b.sessionTime)) ? 1 : -1,
            )
          let nextSession = eventSessions[0]

          if (!nextSession) {
            return <></>
          }

          let nextSessionDate = dayjs(nextSession.sessionTime).format('LL')
          let nextSessionTime = dayjs(nextSession.sessionTime).format('h:mm A')

          return (
            <Card
              key={'event-' + event.id}
              variant="outlined"
              sx={{ minWidth: 280 }}
            >
              <CardMedia
                sx={{
                  objectFit: 'contain',
                  height: 180,
                  display: 'flex',
                  justifyContent: 'space-between',
                  p: 1,
                }}
                image={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${event.thumbnail?.replace('originals', 'resized')}`}
                title={event.name}
              >
                <Chip
                  // color="secondary"
                  sx={{ alignSelf: 'flex-end', fontSize: 14 }}
                  icon={<ClockIcon />}
                  label={duration}
                />
                <Chip
                  // color="primary"
                  sx={{ fontSize: 14 }}
                  label={event.eventType}
                />
              </CardMedia>
              <CardContent>
                <Typography gutterBottom sx={{ fontSize: 18 }}>
                  {event.name}
                </Typography>
                <Typography variant="subtitle2">
                  By {nextSession?.instructor || 'Instructor'}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    mb: -1,
                    mt: 1,
                  }}
                >
                  <CalendarIcon sx={{ height: 16, width: 16 }} />
                  <Typography variant="subtitle2">
                    {nextSessionDate} {'\u2022'} {nextSessionTime}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )
        })}
        {!isLoaded && !featuredEvents.length && (
          <>
            <Skeleton variant="rounded" height={300} />
            <Skeleton variant="rounded" height={300} />
            <Skeleton variant="rounded" height={300} />
          </>
        )}
      </Box>
      <Box sx={{ mb: 2, mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <TextField
          label="Search"
          size="small"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControl sx={{ minWidth: 156 }}>
          <Select
            value={eventTypeFilter}
            onChange={(e) =>
              setEventTypeFilter(e.target.value as EventTypeName)
            }
            size="small"
          >
            <MenuItem value="All">All Event Types</MenuItem>
            <MenuItem value="Virtual">Virtual</MenuItem>
            <MenuItem value="Hybrid">Hybrid</MenuItem>
            <MenuItem value="In Person">In Person</MenuItem>
          </Select>
        </FormControl>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            sx={{ width: 160 }}
            label="From"
            value={dayjs(dateLowerFilter)}
            slotProps={{ textField: { size: 'small' } }}
            onChange={(newValue) => setDateLowerFilter(newValue)}
          />
          <DatePicker
            sx={{ width: 160 }}
            label="To"
            value={dayjs(dateUpperFilter)}
            slotProps={{ textField: { size: 'small' } }}
            onChange={(newValue) => setDateUpperFilter(newValue)}
          />
        </LocalizationProvider>
        <FormControl sx={{ minWidth: 156 }}>
          <Select
            value={accreditationFilter}
            onChange={(e) => setAccreditationFilter(e.target.value as any)}
            size="small"
          >
            <MenuItem value="All">All Accreditations</MenuItem>
            {Object.entries(accreditationDict).map(([val, txt]) => (
              <MenuItem key={val} value={val}>
                {txt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 1 }}></Box>
        <ToggleButtonGroup
          size="small"
          value={viewMode}
          exclusive
          onChange={(_, value) => setViewMode(value)}
        >
          <ToggleButton value="List" sx={{ px: 1, width: 115 }}>
            <BulletedListIcon sx={{ mr: 0.5, height: 18, width: 18 }} />
            <span>List View</span>
          </ToggleButton>
          <ToggleButton value="Calendar" sx={{ px: 1, width: 115 }} disabled>
            <CalendarIcon sx={{ mr: 0.5, height: 18, width: 18 }} />
            <span>Calendar</span>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box>
        {sortedMonths.map((month) => (
          <Box
            key={month}
            sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}
          >
            <Typography sx={{ mb: -0.5 }}>{month}</Typography>
            {sessionsByMonth[month]
              ?.sort((a, b) =>
                dayjs(a.sessionTime).isAfter(dayjs(b.sessionTime)) ? 1 : -1,
              )
              .map((session) => {
                let event = events.find((e) => e.id === session.eventID)
                return (
                  <Paper
                    key={session.id}
                    onClick={() => navigate(`/events/${event?.id}`)}
                    sx={{
                      p: 1,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      alignItems: 'center',
                      '&:hover': {
                        cursor: 'pointer',
                        bgcolor: 'background.paperDark',
                      },
                    }}
                  >
                    <Box sx={{ textAlign: 'center', mx: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 500,
                          mb: -0.75,
                          mt: 0.5,
                        }}
                      >
                        {month.slice(0, 3)}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 24, fontWeight: 500 }}
                        color="primary"
                      >
                        {dayjs(session.sessionTime).format('DD')}
                      </Typography>
                    </Box>
                    <Avatar
                      variant="rounded"
                      src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${event?.thumbnail?.replace('originals', 'resized')}`}
                    />
                    <Box sx={{ flexGrow: 1, ml: 1 }}>
                      <Typography sx={{ mb: 0.5 }}>{event?.name}</Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1.5,
                          fontSize: 14,
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <ClockIcon
                            sx={{ height: 18, width: 18, mb: '2px', mr: '2px' }}
                          />
                          <Box component="span" color="text.secondary">
                            {dayjs(session.sessionTime).format('h:mm A')}
                          </Box>
                        </Box>
                        <Box>
                          <LocationIcon
                            sx={{ height: 18, width: 18, mb: '2px', mr: '1px' }}
                          />
                          <Box component="span" color="text.secondary">
                            {event?.eventType}
                          </Box>
                        </Box>
                        <Box>
                          <UserIcon
                            sx={{ height: 18, width: 18, mb: '2px', mr: '1px' }}
                          />
                          <Box component="span" color="text.secondary">
                            {session.instructor}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    {roles.includes('creator') && (
                      <Box sx={{ mr: 1 }}>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/events/${event?.id}/edit`)
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => setDeleteEvent(event)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                )
              })}
          </Box>
        ))}
      </Box>
      {roles.includes('manager') && unscheduledEvents.length > 0 && (
        <Box sx={{ my: 2 }}>
          <Typography sx={{ mb: 0.5 }}>Unscheduled Events</Typography>
          <Box sx={{ maxHeight: 400, overflow: 'scroll' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {unscheduledEvents.map((event) => (
                <Card
                  key={event.id}
                  sx={{ p: 1.5, display: 'flex', gap: 2, alignItems: 'center' }}
                >
                  <Avatar
                    variant="rounded"
                    src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${event?.thumbnail?.replace('originals', 'resized')}`}
                  />
                  <Typography sx={{ flexGrow: 1 }}>{event.name}</Typography>
                  <Box sx={{ mr: 0.5 }}>
                    <IconButton
                      onClick={() => navigate(`/events/${event?.id}/edit`)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => setDeleteEvent(event)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              ))}
            </Box>
          </Box>
        </Box>
      )}
      {isLoaded &&
        sortedMonths.length === 0 &&
        unscheduledEvents.length === 0 && (
          <Typography>There are no events matching these filters.</Typography>
        )}
      {!isLoaded && (
        <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[1, 2, 3].map((x) => (
            <React.Fragment key={x}>
              <Typography variant="h5">
                <Skeleton />
              </Typography>
              <Skeleton variant="rounded" height={68} />
              <Skeleton variant="rounded" height={68} />
            </React.Fragment>
          ))}
        </Box>
      )}
      <Dialog onClose={() => setDeleteEvent(undefined)} open={!!deleteEvent}>
        <Paper className="px-3 py-1" sx={{ width: 300 }}>
          <DialogTitle sx={{ textAlign: 'center' }}>
            Confirm Event Deletion
          </DialogTitle>
          <DialogContent className="px-2">
            Are you sure you want to delete {deleteEvent?.name}?
          </DialogContent>
          <DialogActions className="flex justify-center pt-0 pb-3">
            <Button
              onClick={() => {
                setLoading(true)
                axios
                  .delete(`/api/event/${deleteEvent?.id}`)
                  .then(() => dispatch(fetchEvents()))
                  .then(() => dispatch(fetchSessions()))
                  .catch((err) => {
                    setErrorMessage(err.message)
                  })
                  .finally(() => {
                    setDeleteEvent(undefined)
                    setLoading(false)
                  })
              }}
              color="primary"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              loading={loading}
              startIcon={<ConfirmIcon />}
            >
              Confirm
            </Button>
            <Button
              onClick={() => setDeleteEvent(undefined)}
              color="error"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </Paper>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  ) : (
    <></>
  )
}
